// React bindings. `<I18nProvider i18n={...}>` threads an I18n instance down;
// `useTranslation()` returns bound `t`/`tc` + current locale.
import { createContext, createElement, useContext } from 'react';
import type { ReactNode } from 'react';
import type { I18n, Locale } from '../types.js';

interface I18nContextValue {
  i18n: I18n;
}

const I18nContext = createContext<I18nContextValue | null>(null);
I18nContext.displayName = 'GovardI18nContext';

export interface I18nProviderProps {
  i18n: I18n;
  children?: ReactNode;
}

export function I18nProvider(props: I18nProviderProps): ReactNode {
  return createElement(I18nContext.Provider, { value: { i18n: props.i18n } }, props.children);
}

export interface UseTranslationResult {
  t: I18n['t'];
  tc: I18n['tc'];
  locale: Locale;
}

export function useTranslation(): UseTranslationResult {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('[@govard/i18n-core] useTranslation must be used within <I18nProvider>.');
  }
  const { i18n } = ctx;
  return {
    t: i18n.t.bind(i18n),
    tc: i18n.tc.bind(i18n),
    locale: i18n.locale,
  };
}

export { I18nContext };
